import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useJWTAuth } from '../components/JWTAuthContext.js';
import { SuccessModal, ErrorModal } from '../components/common';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { isSuccessResponse, getResponseData, getResponseMessage } from '../components/apiClient.js';

interface ModalData {
  title: string;
  message: string;
  buttonText: string;
  secondaryButton?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading: authLoading, setAuthData } = useJWTAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [loginSuccessMobileVerified, setLoginSuccessMobileVerified] = useState<boolean | null>(null);
  
  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showGoogleSignupModal, setShowGoogleSignupModal] = useState(false);
  const [modalData, setModalData] = useState<ModalData>({
    title: '',
    message: '',
    buttonText: ''
  });
  
  // Google user data for potential signup redirect
  const [googleUserData, setGoogleUserData] = useState<any>(null);

  // Check for pending Google signup data on component mount
  useEffect(() => {
    const pendingGoogleData = sessionStorage.getItem('pendingGoogleSignup');
    if (pendingGoogleData) {
      try {
        const userData = JSON.parse(pendingGoogleData);
        setGoogleUserData(userData);
        // Clear the stored data
        sessionStorage.removeItem('pendingGoogleSignup');
        
        // Show helpful message about continuing with Google
        setModalData({
          title: 'Continue with Google',
          message: `Welcome back! You can continue with your Google account (${userData.email}) or try a different login method.`,
          buttonText: 'Got it'
        });
        setShowSuccessModal(true);
      } catch (error) {
        console.error('Error parsing pending Google data:', error);
        sessionStorage.removeItem('pendingGoogleSignup');
      }
    }
  }, []);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
    agreeToTerms: true
  });

  // Redirect if already authenticated (but not during login process)
  useEffect(() => {
    if (isAuthenticated && !authLoading && !isLoading) {
      console.log('User already authenticated, redirecting...');
      // Check mobile verification before redirecting
      const mobileVerified = sessionStorage.getItem('wiziai_mobile_verified') === 'true';
      const destination = mobileVerified ? '/dashboard' : '/verify-mobile';
      console.log('Auto-redirect - mobile_verified:', mobileVerified, 'destination:', destination);
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, authLoading, isLoading, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.email || !formData.password) {
      setModalData({
        title: 'Missing Information',
        message: 'Please fill in all fields to continue with your login.',
        buttonText: 'Okay'
      });
      setShowErrorModal(true);
      return;
    }

    // Validate email format
    if (!formData.email.includes('@')) {
      setModalData({
        title: 'Invalid Email',
        message: 'Please enter a valid email address to login.',
        buttonText: 'Fix Email'
      });
      setShowErrorModal(true);
      return;
    }

    // Validate terms agreement
    if (!formData.agreeToTerms) {
      setModalData({
        title: 'Terms and Conditions Required',
        message: 'Please agree to our terms and conditions to continue.',
        buttonText: 'I Understand'
      });
      setShowErrorModal(true);
      return;
    }

    setIsLoading(true);

    try {
      // Prepare login data for JWT authentication
      const loginData = {
        username: formData.email,
        password: formData.password
      };

      // Use JWT authentication from context
      const result = await login(loginData);
      
      if (result.success) {
        // Small delay to ensure sessionStorage is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Check mobile verification status from sessionStorage
        const mobileVerified = sessionStorage.getItem('wiziai_mobile_verified') === 'true';
        setLoginSuccessMobileVerified(mobileVerified);
        
        console.log('Login success - mobile_verified:', mobileVerified, 'sessionStorage value:', sessionStorage.getItem('wiziai_mobile_verified'));
        
        // Show success modal
        setModalData({
          title: 'Welcome Back! 🎉',
          message: 'You have successfully logged in to WiziAI. Get ready to continue your learning journey!',
          buttonText: mobileVerified ? 'Go to Dashboard' : 'Verify Mobile'
        });
        setShowSuccessModal(true);
      } else {
        const errorMessage = result.message || 'Login failed. Please check your credentials and try again.';
        setModalData({
          title: 'Login Failed',
          message: errorMessage,
          buttonText: 'Try Again'
        });
        setShowErrorModal(true);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = error.message || 'An error occurred during login. Please check your connection and try again.';
      setModalData({
        title: 'Connection Error',
        message: errorMessage,
        buttonText: 'Retry'
      });
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Google login handler  
  const handleGoogleLogin = async (result: any, userProfile?: any) => {
    if (isLoading) return;

    // Store Google user data for potential signup redirect
    if (userProfile) {
      setGoogleUserData(userProfile);
    }

    try {
      if (isSuccessResponse(result)) {
        const userData = getResponseData(result);
        
        // Set user data in JWT context
        if (userData && userData.access_token) {
          // Add mobile_verified to user object before setting auth data
          const userWithMobileVerified = {
            ...userData.user,
            mobile_verified: userData.mobile_verified
          };
          
          // Use auth context to set user data and tokens
          const authResult = setAuthData(
            userWithMobileVerified, 
            userData.access_token, 
            userData.refresh_token,
            userData.expires_in,
            userData.refresh_expires_in
          );
          
          if (authResult.success) {
            // Check mobile verification status from response
            const mobileVerified = userData.mobile_verified === true || userData.mobile_verified === 1 || sessionStorage.getItem('wiziai_mobile_verified') === 'true';
            setLoginSuccessMobileVerified(mobileVerified);
            
            console.log('Google login success - mobile_verified:', mobileVerified);
            
            // Show success modal
            setModalData({
              title: 'Welcome Back! 🎉',
              message: `Hi ${userData.user?.first_name || 'there'}! You have successfully logged in with Google.`,
              buttonText: mobileVerified ? 'Go to Dashboard' : 'Verify Mobile'
            });
            setShowSuccessModal(true);
          } else {
            throw new Error('Failed to set authentication data');
          }
        } else {
          throw new Error('Invalid response from Google login');
        }
      } else {
        const errorMessage = getResponseMessage(result) || 'Google login failed. Please try again.';
        
        // Check if the error indicates no account found
        const isAccountNotFound = errorMessage.toLowerCase().includes('login failed') || 
                                 errorMessage.toLowerCase().includes('not found') ||
                                 errorMessage.toLowerCase().includes('invalid credentials');
        
        if (isAccountNotFound) {
          // Guide user to create account
          setModalData({
            title: 'Account Not Found',
            message: `No WiziAI account found for your Google email. Would you like to create a new account with your Google profile?`,
            buttonText: 'Create Account',
            secondaryButton: 'Try Different Email'
          });
          setShowGoogleSignupModal(true);
        } else {
          // Generic error handling
          setModalData({
            title: 'Google Login Failed',
            message: errorMessage,
            buttonText: 'Try Again'
          });
          setShowErrorModal(true);
        }
      }
    } catch (error: any) {
      console.error('Google login error:', error);
      const errorMessage = error.message || 'An error occurred during Google login. Please try again.';
      setModalData({
        title: 'Google Login Error',
        message: errorMessage,
        buttonText: 'Try Again'
      });
      setShowErrorModal(true);
    }
  };

  const handleGoogleLoginError = (error: Error) => {
    console.error('Google login error:', error);
    setModalData({
      title: 'Google Login Failed',
      message: error.message || 'Failed to authenticate with Google. Please try again.',
      buttonText: 'Try Again'
    });
    setShowErrorModal(true);
  };

  // Modal handlers
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    
    console.log('handleSuccessModalClose - loginSuccessMobileVerified:', loginSuccessMobileVerified);
    
    // Check mobile verification status and redirect accordingly
    if (loginSuccessMobileVerified === false) {
      console.log('Redirecting to mobile verification');
      navigate('/verify-mobile', { replace: true });
    } else if (loginSuccessMobileVerified === true) {
      // Redirect to the page they were trying to access, or dashboard
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      console.log('Redirecting to:', from);
      navigate(from, { replace: true });
    } else {
      // If null (shouldn't happen), default to verify-mobile for safety
      console.log('Mobile verification status unknown, redirecting to verify-mobile');
      navigate('/verify-mobile', { replace: true });
    }
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
  };

  return (
    <div className="min-vh-100 d-flex align-items-center" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 col-xl-5">
            <div className="card border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="card-body p-5">
                {/* Logo and Header */}
                <div className="text-center mb-4">
                  <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                    style={{ 
                      width: '80px', 
                      height: '80px',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)'
                    }}>
                    <img src="/logo.png" alt="wiziai Logo" style={{ border: '2px solid white', borderRadius: '10%', width: '40px', height: '40px' }} />
                  </div>
                  <h2 className="fw-bold mb-2" style={{ 
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Welcome Back!
                  </h2>
                  <p className="text-muted mb-0">Sign in to continue your learning journey</p>
                </div>

                {/* Google Login Section */}
                <div className="d-flex justify-content-center text-center mb-4">
                  <GoogleLoginButton
                    onSuccess={handleGoogleLogin}
                    onError={handleGoogleLoginError}
                    isLoading={isLoading}
                  />
                  
                  {/* <div className="my-3">
                    <hr className="w-100" />
                    <span className="px-3 text-muted bg-white position-relative" style={{ top: '-12px' }}>
                      or
                    </span>
                  </div> */}
                </div>

                {/* Login Form */}
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label htmlFor="email" className="form-label fw-medium">
                      <i className="fas fa-envelope me-2 text-primary"></i>
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="form-control form-control-lg"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      required
                      style={{ 
                        borderRadius: '12px',
                        border: '2px solid #e9ecef',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="password" className="form-label fw-medium">
                      <i className="fas fa-lock me-2 text-primary"></i>
                      Password
                    </label>
                    <input
                      type="password"
                      className="form-control form-control-lg"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter your password"
                      required
                      style={{ 
                        borderRadius: '12px',
                        border: '2px solid #e9ecef',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    />
                  </div>

                  <div className="mb-4">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="agreeToTerms"
                        name="agreeToTerms"
                        checked={formData.agreeToTerms}
                        onChange={handleInputChange}
                        required
                      />
                      <label className="form-check-label text-muted" htmlFor="agreeToTerms">
                        I agree to the 
                        <a 
                          href="/terms.pdf" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-decoration-none ms-1" 
                          style={{ color: '#667eea' }}
                        >
                          Terms and Conditions
                        </a> and 
                        <a 
                          href="/privacy_policy.pdf" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-decoration-none ms-1" 
                          style={{ color: '#667eea' }}
                        >
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn btn-lg w-100 mb-4 fw-bold"
                    style={{
                      background: isLoading ? '#ccc' : 'linear-gradient(135deg, #667eea, #764ba2)',
                      border: 'none',
                      borderRadius: '12px',
                      color: 'white',
                      transition: 'all 0.3s ease',
                      opacity: isLoading ? 0.7 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}>
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing In...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sign-in-alt me-2"></i>
                        Sign In to WiziAI
                      </>
                    )}
                  </button>
                </form>

                {/* Divider */}
                <div className="text-center mb-4">
                  <div className="d-flex align-items-center">
                    <hr className="flex-grow-1" />
                    <span className="mx-3 text-muted">or</span>
                    <hr className="flex-grow-1" />
                  </div>
                </div>

                {/* Social Login */}
                {/* <div className="d-grid gap-2 mb-4">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary btn-lg"
                    style={{ borderRadius: '12px' }}>
                    <i className="fab fa-google me-2 text-danger"></i>
                    Continue with Google
                  </button>
                </div> */}

                {/* Sign Up Link */}
                <div className="text-center">
                  <p className="text-muted mb-0">
                    New to WiziAI? 
                    <Link 
                      to="/signup" 
                      className="text-decoration-none fw-bold ms-1"
                      style={{ color: '#667eea' }}>
                      Create Account
                    </Link>
                  </p>
                </div>                
              </div>
            </div>

            {/* Additional Info */}
            <div className="text-center mt-4">
              <div className="row g-3">
                <div className="col-4">
                  <div className="text-white opacity-75">
                    <i className="fas fa-users fa-lg mb-2 d-block"></i>
                    <small>15,000+ Students</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="text-white opacity-75">
                    <i className="fas fa-trophy fa-lg mb-2 d-block"></i>
                    <small>98% Success Rate</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="text-white opacity-75">
                    <i className="fas fa-star fa-lg mb-2 d-block"></i>
                    <small>4.9/5 Rating</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        title={modalData.title}
        message={modalData.message}
        buttonText={modalData.buttonText}
        onClose={handleSuccessModalClose}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        title={modalData.title}
        message={modalData.message}
        buttonText={modalData.buttonText}
        onClose={handleErrorModalClose}
      />

      {/* Google Signup Guidance Modal */}
      {showGoogleSignupModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '15px' }}>
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold">{modalData.title}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowGoogleSignupModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">{modalData.message}</p>
              </div>
              <div className="modal-footer border-0">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={() => setShowGoogleSignupModal(false)}
                >
                  {modalData.secondaryButton || 'Cancel'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => {
                    setShowGoogleSignupModal(false);
                    // Store Google data for signup page
                    if (googleUserData) {
                      sessionStorage.setItem('pendingGoogleSignup', JSON.stringify(googleUserData));
                    }
                    navigate('/signup');
                  }}
                >
                  {modalData.buttonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
